/*
 * Copyright 2026 Sony Corporation
 */

import type { Device, DeviceStateResponse } from '@ips-device-verse/twin-service/schema';
import type { CoverageOptions, CoverageStats } from '../coverage/coverage';
import type { SubjectPlacement } from '../coverage/subject';
import { fieldOfView, pixelPitch, type ResolvedOptics } from '../scene/camera-optics';
import type { Transform } from '../scene/types';
import type { CoverageDigest, DeviceDigest, DeviceDigestEntry, Vec3 } from './schema';

/**
 * What the device agent is told (devplan §7.3 item 3).
 *
 * Ids, names, models, poses and whether anything is reporting — plus the
 * scene's extent, because "around the capture volume" has to resolve to metres
 * and there is no constant in this codebase that could tell it (devplan §4.7).
 *
 * Never the scene document: this page cannot edit scenery, so the agent is not
 * given the vocabulary to describe it.
 */

export type { DeviceDigest, DeviceDigestEntry };

const round = (value: number): number => Math.round(value * 100) / 100;

const roundVec = (vec: Vec3): Vec3 => [round(vec[0]), round(vec[1]), round(vec[2])];

const toVec = (transform: Transform | undefined): { position?: Vec3; rotation?: Vec3 } =>
  transform
    ? {
        position: roundVec([transform.position.x, transform.position.y, transform.position.z]),
        rotation: roundVec([transform.rotation.x, transform.rotation.y, transform.rotation.z]),
      }
    : {};

const DEGREES = 180 / Math.PI;

/** Shares to a tenth of a percent: enough to tell 99.9 % from done. */
const share = (value: number): number => Math.round(value * 1000) / 1000;

export const digestCoverage = (
  stats: CoverageStats,
  options: Pick<CoverageOptions, 'targetMmPerPx' | 'minOverlap'>,
  placement: SubjectPlacement
): CoverageDigest => ({
  subject: { position: roundVec([...placement.position]), height: round(placement.height) },
  target_mm_per_px: options.targetMmPerPx,
  min_overlap: options.minOverlap,
  covered: share(stats.covered),
  meets_target: share(stats.meetsTarget),
  meets_overlap: share(stats.meetsOverlap),
  ...(stats.worst ? { coarsest_mm_per_px: round(stats.worst.mmPerPx), coarsest_part: stats.worst.part } : {}),
  least_covered: stats.parts
    .filter((part) => part.uncovered > 0.01)
    .slice(0, 5)
    .map((part) => ({ part: part.name, uncovered: share(part.uncovered) })),
});

const toOptics = (resolved: ResolvedOptics | undefined): Pick<DeviceDigestEntry, 'optics'> => {
  if (!resolved) {
    return {};
  }
  const fov = fieldOfView(resolved);
  return {
    optics: {
      ...resolved.optics,
      horizontal_fov_deg: round(fov.horizontal * DEGREES),
      vertical_fov_deg: round(fov.vertical * DEGREES),
      mm_per_px_at_focus: round(pixelPitch(resolved, resolved.optics.focus_distance_m)),
    },
  };
};

export interface DigestCamerasOptions {
  devices: readonly Device[];
  /** Where each device stands right now, following a drag in progress. */
  poses: Record<string, Transform>;
  states: Record<string, DeviceStateResponse>;
  /** Each device's lens and format, an edit in flight included. */
  optics?: Record<string, ResolvedOptics>;
  bounds: { center: Vec3; radius: number };
  selected?: string;
  /** Present while the coverage heatmap is on. */
  coverage?: CoverageDigest;
}

export const digestDevices = ({
  devices,
  poses,
  states,
  optics,
  bounds,
  selected,
  coverage,
}: DigestCamerasOptions): DeviceDigest => ({
  units: 'm',
  bounds: { center: roundVec(bounds.center), radius: round(bounds.radius) },
  ...(selected ? { selection: selected } : {}),
  devices: devices.map((device) => ({
    device_id: device.device_id,
    name: device.name,
    model: device.model,
    ...toVec(poses[device.device_id]),
    reporting: states[device.device_id]?.state.reported !== undefined,
    ...toOptics(optics?.[device.device_id]),
  })),
  ...(coverage ? { coverage } : {}),
});
